import { Controller } from "@hotwired/stimulus"
import I18n from "../../i18n"

const t = (key) => I18n.t(`erp.sales.js.item_pricing.${key}`)

// Textile pricing for one estimate line: supplier price × coefficient, and the Imbretex
// refresh. Marking prices live in erp--marking-form.
// Connects to data-controller="erp--estimate-item-pricing"
export default class extends Controller {
  static targets = [
    "supplierPrice", "supplierPriceFeedback", "marginRate", "textilePrice",
    "unitPrice", "unitPriceOverride", "manualPriceReason",
    "refreshSupplierPrice"
  ]

  static values = { refreshSupplierPriceUrl: String }

  // Imbretex stores 1 cent when it has not returned a price yet.
  static UNKNOWN_SUPPLIER_PRICE = 0.01

  connect() {
    this._refreshAbortController = null
    this.#renderOverrideState()
  }

  disconnect() {
    this._refreshAbortController?.abort()
  }

  calculateTextilePrice() {
    if (!this.hasSupplierPriceTarget || !this.hasMarginRateTarget) return

    const supplierEuros = parseFloat(this.supplierPriceTarget.value) || 0
    if (supplierEuros <= this.constructor.UNKNOWN_SUPPLIER_PRICE) {
      this.#setSupplierFeedback(t("supplier_price_unknown"), "warning")
      return
    }

    this.#setSupplierFeedback("", "info")

    const marginRate = parseFloat(this.marginRateTarget.value) ||
      parseFloat(this.marginRateTarget.dataset.inheritedMarginRate) || 1
    if (this.hasTextilePriceTarget) {
      this.textilePriceTarget.value = (supplierEuros * marginRate).toFixed(2)
    }

    this.updateUnitPrice()
  }

  updateUnitPrice() {
    if (this.hasUnitPriceOverrideTarget && this.unitPriceOverrideTarget.checked) return
    if (!this.hasUnitPriceTarget || !this.hasTextilePriceTarget) return

    this.unitPriceTarget.value = (parseFloat(this.textilePriceTarget.value) || 0).toFixed(2)
    this.unitPriceTarget.dispatchEvent(new Event("input", { bubbles: true }))
  }

  overrideChanged() {
    const overridden = this.#renderOverrideState()
    if (!overridden) this.updateUnitPrice()
  }

  #renderOverrideState() {
    const overridden = this.hasUnitPriceOverrideTarget && this.unitPriceOverrideTarget.checked
    const item = this.element.closest("[data-erp--estimate-editor-target='item']")
    const customerSupplied = item?.querySelector("[data-customer-supplied-field]")?.value === "1"

    if (this.hasUnitPriceTarget) {
      this.unitPriceTarget.readOnly = !overridden && !customerSupplied
      this.unitPriceTarget.classList.toggle("bg-black/5", !overridden && !customerSupplied)
    }
    if (this.hasManualPriceReasonTarget) {
      this.manualPriceReasonTarget.classList.toggle("hidden", !overridden)
    }
    return overridden
  }

  async refreshSupplierPrice() {
    if (!this.refreshSupplierPriceUrlValue) return

    const item = this.element.closest("[data-erp--estimate-editor-target='item']") || this.element
    const variantId = item.querySelector("input[name*='[product_variant_id]']")?.value
    if (!variantId) return this.#setSupplierFeedback(t("no_variant"), "error")

    this._refreshAbortController?.abort()
    const request = new AbortController()
    this._refreshAbortController = request

    if (this.hasRefreshSupplierPriceTarget) this.refreshSupplierPriceTarget.disabled = true
    this.#setSupplierFeedback(t("refreshing"), "info")

    try {
      const response = await fetch(this.refreshSupplierPriceUrlValue.replace("__ID__", variantId), {
        method: "POST",
        signal: request.signal,
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || ""
        }
      })
      if (request !== this._refreshAbortController) return
      const data = await response.json().catch(() => ({}))
      const currentVariantId = item.querySelector("input[name*='[product_variant_id]']")?.value
      if (currentVariantId !== variantId) return

      if (response.ok && data.supplier_price_cents != null) {
        if (this.hasSupplierPriceTarget) {
          this.supplierPriceTarget.value = (data.supplier_price_cents / 100).toFixed(2)
          this.supplierPriceTarget.dispatchEvent(new Event("input", { bubbles: true }))
        }
        this.#setSupplierFeedback(t("refreshed"), "success")
      } else {
        this.#setSupplierFeedback(data.error || t("refresh_failed"), "error")
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        console.warn("[item-pricing] Supplier price refresh failed:", error)
        this.#setSupplierFeedback(t("network_error"), "error")
      }
    } finally {
      if (request === this._refreshAbortController && this.hasRefreshSupplierPriceTarget) {
        this.refreshSupplierPriceTarget.disabled = false
      }
    }
  }

  #setSupplierFeedback(message, level) {
    if (!this.hasSupplierPriceFeedbackTarget) return

    const colors = {
      info: "text-black/60",
      success: "text-green-700",
      warning: "text-amber-700",
      error: "text-red-600"
    }
    this.supplierPriceFeedbackTarget.textContent = message
    this.supplierPriceFeedbackTarget.className = `block mt-1 text-xs ${colors[level] || colors.info}`
  }
}
